import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Apollo } from 'apollo-angular';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { EnvironmentVariablesService } from './environment-variables.service';
import { LoggingService } from './logging.service';
import { ConstantQueryService } from './constant-query.service';
import { IconSetResponse } from '../models/interfaces/contentful-response.interface';

@Injectable({
  providedIn: 'root'
})

export class ContentfulService {
  private apollo = inject(Apollo);
  private http = inject(HttpClient);
  private evService = inject(EnvironmentVariablesService);
  private loggingService = inject(LoggingService);
  private graphqlquery = inject(ConstantQueryService);

  // To store key for contentful without calling API multiple times
  private contentfulKey$ = new ReplaySubject<string>(1);
  baseURL: string;
  developer!: string;
  cfEndPoint: string;
  get landingPageList(): string[] {
    return ['dashboard', 'series', 'deals', 'assets', 'contactPage', 'clickThroughJourney'];
  }
  IconsetQueryProxy!: string;
  private iconSetState = signal<Record<string, string> | null>(null);
  readonly iconSet = this.iconSetState.asReadonly();
  private iconSetValue$ = toObservable<Record<string, string> | null>(this.iconSetState);

  constructor() {
    this.baseURL = this.evService.proxy['hostname'] + '/' + (this.evService.proxy['apis'] as Record<string, string>)['Contentful'];
    this.cfEndPoint = 'https://graphql.contentful.com/content/v1/spaces/' + this.evService.contentful.spaceId + '/environments/' + this.evService.contentful['environmentMode'];
  }

  getPageNotFound(previewFlag: boolean): Observable<unknown> {
    return this.apollo.watchQuery<unknown>({
      query: this.graphqlquery.pageNotFoundQuery,
      variables: {
        previewFlag: previewFlag,
      },
      errorPolicy: 'all'
    }).valueChanges;
  }

  getLoginPage(previewFlag: boolean): Observable<unknown> {
    return this.apollo.watchQuery<unknown>({
      query: this.graphqlquery.loginLandingPage,
      variables: {
        previewFlag: previewFlag,
      },
      errorPolicy: 'all'
    }).valueChanges;
  }

  proxyErrorHandler(error: HttpErrorResponse) {
    this.loggingService.log("ContentFul API Proxy Error - - ", error);
    return throwError(() => error || "server error.");
  }

  initializeContentfulKey() {
    this.http.get(this.baseURL + "/apikey", {
      responseType: 'text'
    }).pipe(catchError(x => this.proxyErrorHandler(x))).subscribe(res => {
      this.contentfulKey$.next(res);
    })
  }

  fetchCommonAssets(mode: string, id = ''): Observable<unknown> {
    let query = '';
    let variables = {};
    const switchMode = (this.landingPageList.indexOf(mode) < 0) ? mode : 'landingPage';

    const data = this.getAssetQueryAndVariable(switchMode, id);
    query = data.query;
    variables = data.variables;

    const assets$: Observable<unknown> = this.contentfulKey$.pipe(
      switchMap((cfKey): Observable<unknown> => {
        return this.http.post<unknown>(this.cfEndPoint,
          JSON.stringify({ query: query, variables: variables }),
          {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              'authorization': 'Bearer ' + cfKey
            })
          })
          .pipe(catchError(x => this.proxyErrorHandler(x)));
      })
    )
    return assets$
  }

  buildKeyValueLabels(array: Record<string, string>[]) {
    const labelsArray: Record<string, unknown> = {};
    for (const item of array) {
      labelsArray[item['key']] = item['value'];
    }
    return labelsArray;
  }

  buildKeyValueIcons(array: Record<string, unknown>[]): Record<string, string> {
    const iconsSet: Record<string, string> = {};
    for (const item of array) {
      const key = item['key'] as string;
      if (item.iconImage == null) {
      iconsSet[key] = '';
      }
      else {
      iconsSet[key] = (item.iconImage as Record<string, string>)['url'];
      }
    }
    return iconsSet;
  }

  getResourceValue(array: Record<string, unknown>[], key: string, value: unknown) {
    for (const item of array) {
      if (item[key] === value) {
      return item.value;
      }
    }
    return null;
  }

  getAssetQueryAndVariable(assetType: string, id: number | string) {
    const assetQueryAndVariableArr = [{ assetType: 'header', query: this.graphqlquery.headerQuery, variables: { previewFlag: this.evService.contentful.previewFlag } },
    { assetType: 'iconUrl', query: this.graphqlquery.IconSetQuery, variables: { previewFlag: this.evService.contentful.previewFlag } },
    { assetType: 'session', query: this.graphqlquery.sessionTimeOutQuery, variables: { previewFlag: this.evService.contentful.previewFlag } },
    { assetType: 'landingPage', query: this.graphqlquery.landingPageByIDQuery, variables: { id: id, previewFlag: this.evService.contentful.previewFlag } },
    { assetType: 'investmentManager', query: this.graphqlquery.InvestmentManagerQuery, variables: { masterId: Number(id), previewFlag: this.evService.contentful.previewFlag } },
    { assetType: 'assetSummary', query: this.graphqlquery.AssetSummaryQuery, variables: { masterId: Number(id), previewFlag: this.evService.contentful.previewFlag } }]

    const assetData = assetQueryAndVariableArr.filter(x => x.assetType === assetType)[0];
    return assetData;
  }

  setIconSetValue(val: IconSetResponse) {
    this.iconSetState.set(this.buildKeyValueIcons(val?.data?.iconCollection?.items ?? []));
  }

  getIconSetValue(): Observable<Record<string, string> | null> {
    return this.iconSetValue$;
  }
}
